import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, CardBody, CardHeader, IconButton, Select, Spinner, formInputClassName } from './common';
import { familyTreesAPI, inviteLinksAPI, InviteLinkDto, TreeMemberDto, treeMembersAPI } from '../services/api';
import { brandClasses } from '../theme';
import { useTranslation } from 'react-i18next';
import { useFormatDate, useFormatDateTime } from '../i18n';
import {
  canManageMembership,
  canRemoveMember,
  getInviteStatus,
  getInviteStatusTone,
  getManageableRoleOptions,
} from '../utils/memberManagement';

interface MembersPanelProps {
  treeId: string;
  treeName: string;
  currentUserId?: string;
  currentUserRole?: TreeMemberDto['role'];
  canManageMembers: boolean;
  canTransferOwnership?: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

const isForbiddenError = (error: unknown) => {
  return typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: { status?: number } }).response?.status === 'number' &&
    (error as { response?: { status?: number } }).response?.status === 403;
};

const extractPayload = <T,>(payload: T | { data?: T | { data?: T } }): T => {
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    const wrapped = payload as { data?: T | { data?: T } };
    return (typeof wrapped.data === 'object' && wrapped.data !== null && 'data' in wrapped.data
      ? (wrapped.data as { data?: T }).data
      : wrapped.data) as T;
  }

  return payload as T;
};

const extractErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const responseData = (error as { response?: { data?: { detail?: string; error?: string; message?: string } } }).response?.data;
    return responseData?.detail || responseData?.error || responseData?.message || fallback;
  }

  return fallback;
};

type ApiErrorResponse = {
  response: {
    status: number;
    data: { code: string; existingInviteId?: string };
  };
};

const extractConflictPayload = (error: unknown): { code: string; existingInviteId?: string } | null => {
  if (typeof error !== 'object' || error === null || !('response' in error)) return null;
  const resp = (error as ApiErrorResponse).response;
  if (resp?.status !== 409) return null;
  return { code: resp.data?.code ?? '', existingInviteId: resp.data?.existingInviteId };
};

const INVITE_ROLE_OPTIONS: Array<{ value: TreeMemberDto['role']; label: string }> = [
  { value: 'Viewer', label: 'Viewer' },
  { value: 'Contributor', label: 'Contributor' },
  { value: 'Editor', label: 'Editor' },
  { value: 'Manager', label: 'Manager' },
];

export const MembersPanel: React.FC<MembersPanelProps> = ({
  treeId,
  treeName,
  currentUserId,
  currentUserRole,
  canManageMembers,
  canTransferOwnership = false,
  onClose,
  onUpdated,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const formatDate = useFormatDate();
  const formatDateTime = useFormatDateTime();
  const [members, setMembers] = useState<TreeMemberDto[]>([]);
  const [invites, setInvites] = useState<InviteLinkDto[]>([]);
  const [membersLoading, setMembersLoading] = useState(true);
  const [invitesLoading, setInvitesLoading] = useState(true);
  const [membersError, setMembersError] = useState('');
  const [invitesError, setInvitesError] = useState('');
  const [inviteAccessRestricted, setInviteAccessRestricted] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [pendingConflict, setPendingConflict] = useState<{ code: string; existingInviteId?: string } | null>(null);
  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [inviteStatusFilter, setInviteStatusFilter] = useState<'Active' | 'Revoked' | 'Expired' | 'Used' | 'All'>('Active');
  const [inviteEmailSearch, setInviteEmailSearch] = useState('');
  const [selectedTransferUserId, setSelectedTransferUserId] = useState('');
  const resolvedCurrentUserRole = useMemo<TreeMemberDto['role'] | undefined>(() => {
    if (!currentUserId) {
      return currentUserRole;
    }

    return members.find((member) => member.userId === currentUserId)?.role ?? currentUserRole;
  }, [currentUserId, currentUserRole, members]);
  const effectiveCanManageMembers = canManageMembership(resolvedCurrentUserRole) || canManageMembers;
  const ownershipTransferCandidates = members.filter((member) => member.role !== 'Owner');
  const [createInvite, setCreateInvite] = useState({
    inviteeEmail: '',
    role: effectiveCanManageMembers && resolvedCurrentUserRole === 'Owner' ? 'Manager' : 'Editor',
    expireDays: 7,
  });

  const availableInviteRoles = useMemo(() => {
    const baseRoles = resolvedCurrentUserRole === 'Owner'
      ? INVITE_ROLE_OPTIONS
      : INVITE_ROLE_OPTIONS.filter((option) => option.value !== 'Manager');

    return baseRoles.map((option) => ({
      value: option.value,
      label: option.label,
    }));
  }, [resolvedCurrentUserRole]);

  useEffect(() => {
    if (!availableInviteRoles.some((option) => option.value === createInvite.role)) {
      setCreateInvite((currentInvite) => ({
        ...currentInvite,
        role: availableInviteRoles[0]?.value ?? 'Editor',
      }));
    }
  }, [availableInviteRoles, createInvite.role]);

  const loadMembers = useCallback(async () => {
    try {
      setMembersLoading(true);
      setMembersError('');
      const response = await treeMembersAPI.getMembers(treeId);
      setMembers(extractPayload<TreeMemberDto[]>(response.data) ?? []);
    } catch (error) {
      setMembersError(extractErrorMessage(error, 'Failed to load tree members.'));
      setMembers([]);
    } finally {
      setMembersLoading(false);
    }
  }, [treeId]);

  const loadInvites = useCallback(async () => {
    if (!effectiveCanManageMembers) {
      setInvites([]);
      setInvitesError('');
      setInviteAccessRestricted(false);
      setInvitesLoading(false);
      return;
    }

    try {
      setInvitesLoading(true);
      setInvitesError('');
      setInviteAccessRestricted(false);
      const response = await inviteLinksAPI.getInviteLinks(treeId, {
        status: inviteStatusFilter,
        email: inviteEmailSearch.trim() || undefined,
      });
      setInvites(extractPayload<InviteLinkDto[]>(response.data) ?? []);
    } catch (error) {
      if (isForbiddenError(error)) {
        setInviteAccessRestricted(true);
        setInvites([]);
      } else {
        setInvitesError(extractErrorMessage(error, 'Failed to load invite links.'));
      }
    } finally {
      setInvitesLoading(false);
    }
  }, [effectiveCanManageMembers, inviteEmailSearch, inviteStatusFilter, treeId]);

  const reloadPanel = useCallback(async () => {
    await loadMembers();
    if (effectiveCanManageMembers) {
      await loadInvites();
    }
  }, [effectiveCanManageMembers, loadInvites, loadMembers]);

  useEffect(() => {
    void loadMembers();
  }, [loadMembers]);

  const handleRoleChange = async (memberId: string, newRole: string) => {
    try {
      setBusyKey(`role:${memberId}`);
      setMembersError('');
      await treeMembersAPI.updateMemberRole(memberId, newRole);
      await reloadPanel();
      onUpdated?.();
    } catch (error) {
      setMembersError(extractErrorMessage(error, 'Failed to update member role.'));
    } finally {
      setBusyKey('');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      setBusyKey(`remove:${memberId}`);
      setMembersError('');
      await treeMembersAPI.removeMember(memberId);
      await reloadPanel();
      onUpdated?.();
    } catch (error) {
      setMembersError(extractErrorMessage(error, 'Failed to remove member.'));
    } finally {
      setBusyKey('');
    }
  };

  const handleCreateInvite = async (event: React.FormEvent) => {
    event.preventDefault();
    setPendingConflict(null);

    try {
      setBusyKey('create-invite');
      setInvitesError('');
      const response = await inviteLinksAPI.createInviteLink({
        treeId,
        inviteeEmail: createInvite.inviteeEmail.trim(),
        role: createInvite.role,
        expireDays: createInvite.expireDays,
      });

      const createdInvite = extractPayload<InviteLinkDto>(response.data);
      setInvites((currentInvites) => [createdInvite, ...currentInvites]);
      setShowInviteDialog(false);
      setCreateInvite((currentInvite) => ({
        ...currentInvite,
        inviteeEmail: '',
        expireDays: 7,
      }));
      await loadInvites();
    } catch (error) {
      const conflict = extractConflictPayload(error);
      if (conflict) {
        setPendingConflict(conflict);
      } else {
        setInvitesError(extractErrorMessage(error, 'Failed to create invite.'));
      }
    } finally {
      setBusyKey('');
    }
  };

  const handleConflictResend = async () => {
    if (!pendingConflict?.existingInviteId) return;
    try {
      setBusyKey(`resend:${pendingConflict.existingInviteId}`);
      setPendingConflict(null);
      setInvitesError('');
      await inviteLinksAPI.resendInviteLink(pendingConflict.existingInviteId);
      setShowInviteDialog(false);
      setCreateInvite((c) => ({ ...c, inviteeEmail: '', expireDays: 7 }));
      await loadInvites();
    } catch (error) {
      setInvitesError(extractErrorMessage(error, 'Failed to resend invite.'));
    } finally {
      setBusyKey('');
    }
  };

  const handleConflictRevoke = async () => {
    if (!pendingConflict?.existingInviteId) return;
    try {
      setBusyKey(`revoke:${pendingConflict.existingInviteId}`);
      setPendingConflict(null);
      setInvitesError('');
      await inviteLinksAPI.revokeInviteLink(pendingConflict.existingInviteId);
      await loadInvites();
    } catch (error) {
      setInvitesError(extractErrorMessage(error, 'Failed to revoke invite.'));
    } finally {
      setBusyKey('');
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    try {
      setBusyKey(`revoke:${inviteId}`);
      setInvitesError('');
      await inviteLinksAPI.revokeInviteLink(inviteId);
      await loadInvites();
    } catch (error) {
      setInvitesError(extractErrorMessage(error, 'Failed to revoke invite.'));
    } finally {
      setBusyKey('');
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    try {
      setBusyKey(`resend:${inviteId}`);
      setInvitesError('');
      await inviteLinksAPI.resendInviteLink(inviteId);
      await loadInvites();
    } catch (error) {
      setInvitesError(extractErrorMessage(error, 'Failed to resend invite.'));
    } finally {
      setBusyKey('');
    }
  };

  useEffect(() => {
    if (!effectiveCanManageMembers) {
      setInvites([]);
      setInvitesLoading(false);
      setInvitesError('');
      setInviteAccessRestricted(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      void loadInvites();
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [effectiveCanManageMembers, inviteEmailSearch, inviteStatusFilter, loadInvites]);

  const inviteDialogCanSubmit = createInvite.inviteeEmail.trim().length > 0;
  const transferDialogCanSubmit = selectedTransferUserId.trim().length > 0;

  const handleTransferOwnership = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      setBusyKey('transfer-ownership');
      setMembersError('');
      await familyTreesAPI.transferOwnership(treeId, selectedTransferUserId);
      setShowTransferDialog(false);
      setSelectedTransferUserId('');
      await reloadPanel();
      onUpdated?.();
    } catch (error) {
      setMembersError(extractErrorMessage(error, 'Failed to transfer ownership.'));
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="relative flex h-full flex-col">
      <div className={`flex items-center justify-between border-b px-6 py-5 ${brandClasses.subtleBorder}`}>
        <div>
          <h2 className={`text-xl font-semibold ${brandClasses.primaryText}`}>{t('panel.title', { ns: 'members' })}</h2>
          <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{t('panel.subtitle', { treeName, ns: 'members' })}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate(`/tree/${treeId}/members`)}
          >
            Member Details
          </Button>
          <IconButton
            type="button"
            onClick={onClose}
            aria-label={t('panel.closeAriaLabel', { ns: 'members' })}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          </IconButton>
        </div>
      </div>

      <div className="flex-1 space-y-6 overflow-y-auto p-6">
        <Card className={brandClasses.elevatedCard}>
          <CardHeader className="flex items-center justify-between">
            <div>
              <h3 className={`text-lg font-semibold ${brandClasses.primaryText}`}>{t('panel.currentMembers', { ns: 'members' })}</h3>
              <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{t('panel.currentMembersSubtitle', { ns: 'members' })}</p>
            </div>
            {canTransferOwnership ? (
              <Button type="button" variant="outline" onClick={() => setShowTransferDialog(true)}>
                {t('panel.transferOwnership', { ns: 'members' })}
              </Button>
            ) : null}
          </CardHeader>
          <CardBody className="space-y-4">
            {membersError && <Alert variant="error" message={membersError} />}

            {membersLoading ? (
              <div className="flex items-center justify-center py-10">
                <Spinner size="lg" />
              </div>
            ) : members.length === 0 ? (
              <p className={`text-sm ${brandClasses.mutedText}`}>No members found for this tree.</p>
            ) : (
              <div className="space-y-3">
                {members.map((member) => {
                  const memberRole = member.role as TreeMemberDto['role'];
                  const isSelf = member.userId === currentUserId;
                  const roleOptions = getManageableRoleOptions(resolvedCurrentUserRole, memberRole, isSelf);
                  const canRemove = canRemoveMember(resolvedCurrentUserRole, memberRole, isSelf);

                  return (
                    <div
                      key={member.id}
                      className={`rounded-xl border px-4 py-4 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className={`break-words font-medium ${brandClasses.primaryText}`}>{member.userEmail}</p>
                          <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>
                            {t('panel.joinedDate', { date: formatDate(member.joinedAt), ns: 'members' })}
                            {isSelf ? ` • ${t('panel.selfIndicator', { ns: 'members' })}` : ''}
                          </p>
                        </div>
                        <div className="shrink-0 self-start">
                          <Badge variant="primary">{member.role}</Badge>
                        </div>
                      </div>

                      {(roleOptions.length > 0 || canRemove) && canManageMembership(resolvedCurrentUserRole) ? (
                        <div className="mt-4 flex flex-col gap-3">
                          {roleOptions.length > 0 && (
                            <Select
                              aria-label={`Role for ${member.userEmail}`}
                              value={member.role}
                              options={roleOptions.map((role) => ({ value: role, label: role }))}
                              onChange={(event) => void handleRoleChange(member.id, event.target.value)}
                              disabled={busyKey === `role:${member.id}`}
                            />
                          )}

                          {canRemove && (
                            <Button
                              variant="danger"
                              size="sm"
                              isLoading={busyKey === `remove:${member.id}`}
                              onClick={() => void handleRemoveMember(member.id)}
                            >
                              {t('panel.removeMember', { ns: 'members' })}
                            </Button>
                          )}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            )}
          </CardBody>
        </Card>

        {effectiveCanManageMembers ? (
          <Card className={brandClasses.elevatedCard}>
            <CardHeader className="flex items-center justify-between gap-4">
              <div>
                <h3 className={`text-lg font-semibold ${brandClasses.primaryText}`}>{t('panel.pendingInvites', { ns: 'members' })}</h3>
                <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>{t('panel.pendingInvitesSubtitle', { ns: 'members' })}</p>
              </div>
              <Button type="button" onClick={() => setShowInviteDialog(true)}>
                {t('panel.inviteMember', { ns: 'members' })}
              </Button>
            </CardHeader>
            <CardBody className="space-y-4">
              {invitesError && <Alert variant="error" message={invitesError} />}

              {!invitesLoading && !inviteAccessRestricted ? (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <Select
                    label={t('panel.inviteStatus', { ns: 'members' })}
                    value={inviteStatusFilter}
                    options={[
                      { value: 'Active', label: t('panel.inviteStatusOptions.active', { ns: 'members' }) },
                      { value: 'Revoked', label: t('panel.inviteStatusOptions.revoked', { ns: 'members' }) },
                      { value: 'Expired', label: t('panel.inviteStatusOptions.expired', { ns: 'members' }) },
                      { value: 'Used', label: t('panel.inviteStatusOptions.used', { ns: 'members' }) },
                      { value: 'All', label: t('panel.inviteStatusOptions.all', { ns: 'members' }) },
                    ]}
                    onChange={(event) => setInviteStatusFilter(event.target.value as typeof inviteStatusFilter)}
                  />

                  <label className={`block text-sm font-medium ${brandClasses.primaryText}`}>
                    {t('panel.searchByEmail', { ns: 'members' })}
                    <input
                      type="text"
                      value={inviteEmailSearch}
                      onChange={(event) => setInviteEmailSearch(event.target.value)}
                      className={`mt-1 ${formInputClassName}`}
                      placeholder={t('panel.searchByEmailPlaceholder', { ns: 'members' })}
                    />
                  </label>
                </div>
              ) : null}

              {invitesLoading ? (
                <div className="flex items-center justify-center py-10">
                  <Spinner size="lg" />
                </div>
              ) : inviteAccessRestricted ? (
                <p className={`text-sm ${brandClasses.mutedText}`}>
                  {t('panel.noInvitesFound', { ns: 'members' })}
                </p>
              ) : invites.length === 0 ? (
                <p className={`text-sm ${brandClasses.mutedText}`}>
                  {t('panel.noInvitesFound', { ns: 'members' })}
                </p>
              ) : (
                <div className="space-y-3">
                  {invites.map((invite) => {
                    const status = getInviteStatus(invite);
                    const canRevoke = effectiveCanManageMembers && invite.canRevoke && status === 'Active';
                    const canResend = effectiveCanManageMembers && status === 'Expired';

                    const revokedByLabel = invite.revokedByUserId
                      ? (invite.revokedByUserId === currentUserId ? 'You' : `User ${invite.revokedByUserId.slice(0, 8)}…`)
                      : undefined;

                    return (
                      <div
                        key={invite.id}
                        className={`rounded-xl border px-4 py-4 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0 space-y-1">
                            <div className="flex items-center gap-2">
                              <p className={`min-w-0 break-words font-medium ${brandClasses.primaryText}`}>{invite.inviteeEmail}</p>
                              <Badge variant={getInviteStatusTone(status) as 'default' | 'success' | 'warning' | 'primary'}>
                                {status}
                              </Badge>
                            </div>
                <p className={`text-sm ${brandClasses.bodyText}`}>{t('panel.inviteRoleLabel', { role: invite.role, ns: 'members' })}</p>
                            <p className={`text-sm ${brandClasses.mutedText}`}>
                              {t('panel.inviteExpires', { date: formatDateTime(invite.expiresAt), ns: 'members' })}
                            </p>
                            {invite.usedAt ? (
                              <p className={`text-sm ${brandClasses.mutedText}`}>
                                {t('panel.inviteUsed', { date: formatDateTime(invite.usedAt), ns: 'members' })}
                              </p>
                            ) : null}

                            {status === 'Revoked' && invite.revokedAt ? (
                              <p className={`text-sm ${brandClasses.mutedText}`}>
                                {t('panel.inviteRevoked', { date: formatDateTime(invite.revokedAt), ns: 'members' })}{revokedByLabel ? ` • ${revokedByLabel}` : ''}
                              </p>
                            ) : null}
                          </div>

                          <div className="flex flex-col gap-2 self-start sm:flex-shrink-0">
                            {canRevoke ? (
                              <Button
                                variant="outline"
                                size="sm"
                                isLoading={busyKey === `revoke:${invite.id}`}
                                onClick={() => void handleRevokeInvite(invite.id)}
                              >
                                {t('panel.revokeButton', { ns: 'members' })}
                              </Button>
                            ) : null}

                            {canResend ? (
                              <Button
                                variant="outline"
                                size="sm"
                                isLoading={busyKey === `resend:${invite.id}`}
                                onClick={() => void handleResendInvite(invite.id)}
                              >
                                {t('panel.resendButton', { ns: 'members' })}
                              </Button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardBody>
          </Card>
        ) : null}
      </div>

      {showInviteDialog ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[rgba(32,24,16,0.28)] px-6">
          <div className={`w-full max-w-md rounded-2xl border p-6 shadow-2xl ${brandClasses.subtleBorder} ${brandClasses.appShell}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className={`text-lg font-semibold ${brandClasses.primaryText}`}>{t('panel.inviteDialogTitle', { ns: 'members' })}</h3>
                <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>
                  {t('panel.inviteDialogSubtitle', { ns: 'members' })}
                </p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setShowInviteDialog(false)}>
                {t('panel.cancelButton', { ns: 'members' })}
              </Button>
            </div>

            <form className="mt-5 space-y-4" onSubmit={handleCreateInvite}>
              <label className={`block text-sm font-medium ${brandClasses.primaryText}`}>
                {t('panel.inviteDialogEmailLabel', { ns: 'members' })}
                <input
                  type="email"
                  value={createInvite.inviteeEmail}
                  onChange={(event) => { setPendingConflict(null); setCreateInvite((currentInvite) => ({ ...currentInvite, inviteeEmail: event.target.value })); }}
                  className={`mt-1 ${formInputClassName}`}
                  placeholder={t('panel.inviteDialogEmailPlaceholder', { ns: 'members' })}
                  required
                />
              </label>

              <Select
                label="Role"
                value={createInvite.role}
                options={availableInviteRoles}
                onChange={(event) => setCreateInvite((currentInvite) => ({ ...currentInvite, role: event.target.value as TreeMemberDto['role'] }))}
              />

              <label className={`block text-sm font-medium ${brandClasses.primaryText}`}>
                {t('panel.inviteDialogDaysLabel', { ns: 'members' })}
                <input
                  type="number"
                  min={1}
                  max={30}
                  value={createInvite.expireDays}
                  onChange={(event) => {
                    const nextValue = Math.max(1, Math.min(30, Number(event.target.value) || 1));
                    setCreateInvite((currentInvite) => ({ ...currentInvite, expireDays: nextValue }));
                  }}
                  className={`mt-1 ${formInputClassName}`}
                />
              </label>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" type="button" onClick={() => { setShowInviteDialog(false); setPendingConflict(null); }}>
                  {t('panel.cancelButton', { ns: 'members' })}
                </Button>
                <Button type="submit" isLoading={busyKey === 'create-invite'} disabled={!inviteDialogCanSubmit}>
                  {t('panel.inviteMember', { ns: 'members' })}
                </Button>
              </div>

              {/* Conflict banner — shown inline when server rejects with 409 */}
              {pendingConflict && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm">
                  {pendingConflict.code === 'INVITE_ALREADY_MEMBER' && (
                    <p className="font-medium text-amber-800">{t('panel.conflictAlreadyMember', { ns: 'members' })}</p>
                  )}
                  {pendingConflict.code === 'INVITE_PENDING_EXISTS' && (
                    <>
                      <p className="font-medium text-amber-800">{t('panel.conflictPendingExists', { ns: 'members' })}</p>
                      <p className="mt-0.5 text-amber-700">{t('panel.conflictPendingHint', { ns: 'members' })}</p>
                      <div className="mt-3 flex gap-2">
                        <Button
                          type="button"
                          size="sm"
                          isLoading={busyKey.startsWith('resend:')}
                          onClick={() => void handleConflictResend()}
                        >
                          {t('panel.resendInviteButton', { ns: 'members' })}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          isLoading={busyKey.startsWith('revoke:')}
                          onClick={() => void handleConflictRevoke()}
                        >
                          {t('panel.revokeAndDismissButton', { ns: 'members' })}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </form>
          </div>
        </div>
      ) : null}

      {showTransferDialog ? (
        <div className="absolute inset-0 z-20 flex items-center justify-center bg-[rgba(32,24,16,0.28)] px-6">
          <div className={`w-full max-w-xl rounded-2xl border p-6 shadow-2xl ${brandClasses.subtleBorder} ${brandClasses.appShell}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className={`text-lg font-semibold ${brandClasses.primaryText}`}>{t('panel.transferOwnership', { ns: 'members' })}</h3>
                <p className={`mt-1 text-sm ${brandClasses.mutedText}`}>
                  {t('panel.transferSubtitle', { ns: 'members' })}
                </p>
              </div>
              <IconButton
                type="button"
                onClick={() => {
                  setShowTransferDialog(false);
                  setSelectedTransferUserId('');
                }}
                aria-label={t('panel.closeTransferAriaLabel', { ns: 'members' })}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18" />
                  <path d="M6 6l12 12" />
                </svg>
              </IconButton>
            </div>

            <form className="mt-5 space-y-4" onSubmit={handleTransferOwnership}>
              <Select
                label={t('panel.newOwner', { ns: 'members' })}
                value={selectedTransferUserId}
                options={[
                  { value: '', label: t('panel.selectMember', { ns: 'members' }) },
                  ...ownershipTransferCandidates.map((member) => ({
                    value: member.userId,
                    label: `${member.userEmail} (${member.role})`,
                  })),
                ]}
                onChange={(event) => setSelectedTransferUserId(event.target.value)}
                disabled={busyKey === 'transfer-ownership'}
              />

              <div className={`rounded-xl border px-4 py-4 ${brandClasses.subtleBorder} ${brandClasses.mutedSurface}`}>
                <p className={`text-sm font-medium ${brandClasses.primaryText}`}>{t('panel.transferThisWill', { ns: 'members' })}</p>
                <p className={`mt-2 text-sm ${brandClasses.bodyText}`}>{t('panel.transferNoteEffective', { ns: 'members' })}</p>
                <p className={`mt-1 text-sm ${brandClasses.bodyText}`}>{t('panel.transferNotePromote', { ns: 'members' })}</p>
                <p className={`mt-1 text-sm ${brandClasses.bodyText}`}>{t('panel.transferNoteKeep', { ns: 'members' })}</p>
                <p className={`mt-1 text-sm ${brandClasses.bodyText}`}>{t('panel.transferNoteGive', { ns: 'members' })}</p>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowTransferDialog(false);
                    setSelectedTransferUserId('');
                  }}
                >
                  {t('panel.cancelButton', { ns: 'members' })}
                </Button>
                <Button type="submit" variant="danger" isLoading={busyKey === 'transfer-ownership'} disabled={!transferDialogCanSubmit}>
                  {t('panel.confirmTransferButton', { ns: 'members' })}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
};
